/**
 * Лавлах өгөгдөл — `/ref` модулийн бичих хэсэг.
 *
 * Захиалагч өөрөө удирддаг мастер өгөгдөл: загвар · сонголт (lookup) ·
 * ханш · баталгааны нөхцөл · төхөөрөмж.
 *
 * ⚠️ «Урамшуулал» табыг НУУВ (2026-10-05, захиалагчийн цэгцлэл №16) —
 *    бодитоор олгогдож буй эсэх тодорхойгүй. `commission_rules`, `GET/POST
 *    /ref/commission-rules`, `order.confirm`-ийн хувь хөлдөөлт ХЭВЭЭР.
 *    Буцаах бол энэ commit-ийн өмнөх хувилбараас `CommissionTab`-ыг сэргээнэ.
 * ⚠️ Нийлүүлэгч → «Тохиргоо → Нийлүүлэгч». Салбар, байршил, агуулах →
 *    «Тохиргоо → Салбар, агуулах». Хэрэглэгч → «Тохиргоо → Ажилтан».
 *    Тэдгээр нь тус бүрдээ БҮТЭН дэлгэц — энд таб болж давхардахгүй.
 * ⚠️ Баталгааны нөхцөл ЗАСАГДАХГҮЙ — 10 жилийн нотолгоо тул шинэ
 *    хувилбар нэмнэ (CLAUDE.md).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate, formatDateTime, formatQty, formatRate, isAppOutdated } from '@boots/domain';
import type { MobileReleaseOutput } from '@boots/schema';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { GENDER_LABEL, MATERIAL_LABEL, type Store } from '../lib/types';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, Select, Spinner, Tabs, Textarea,
} from '../components/ui';

type Tab = 'models' | 'apps' | 'promos' | 'reasons' | 'colors' | 'fx' | 'terms' | 'devices';

const TABS: { key: Tab; label: string }[] = [
  { key: 'models', label: 'Гутлын загвар' },
  { key: 'apps', label: 'Зээлийн апп' },
  { key: 'promos', label: 'Урамшуулал' },
  { key: 'reasons', label: 'Шалтгаан' },
  { key: 'colors', label: 'Өнгө' },
  { key: 'fx', label: 'Ханш' },
  { key: 'terms', label: 'Баталгааны нөхцөл' },
  { key: 'devices', label: 'Төхөөрөмж' },
];

export function ReferencePage() {
  const { can, readOnly } = useAuth();
  const [tab, setTab] = useState<Tab>('models');
  const canWrite = can('ADMIN', 'WAREHOUSE') && !readOnly;

  return (
    <div className="flex flex-col gap-3">
      <Tabs value={tab} onChange={setTab} tabs={TABS} scroll />
      {/* `key` — гурван жагсаалтын таб нэг компонент; таб солиход дотоод төлөв шинэчлэгдэнэ */}
      {tab === 'models' ? <ModelsTab canWrite={canWrite} />
        : tab === 'apps' ? <LookupsTab key={tab} title="Зээлийн апп" categories={['PAYMENT_PROVIDER']} canWrite={canWrite} />
          : tab === 'promos' ? <LookupsTab key={tab} title="Урамшуулал" categories={['PROMOTION']} canWrite={canWrite} />
          : tab === 'reasons' ? <LookupsTab key={tab} title="Шалтгаан" categories={REASON_CATEGORIES} canWrite={canWrite} />
            : tab === 'colors' ? <LookupsTab key={tab} title="Гутлын өнгө" categories={['COLORWAY']} canWrite={canWrite} />
              : tab === 'fx' ? <FxTab canWrite={canWrite} />
                : tab === 'terms' ? <TermsTab />
                  : <DevicesTab />}
    </div>
  );
}

// ============================================================ Загвар
interface ModelRow {
  id: string; code: string; brand: string; name: string;
  gender: string; material: string; isUnique: boolean;
  sizeMin: number | null; sizeMax: number | null; warrantyDays: number;
  isActive?: boolean;
  colorway: { id: string; labelMn: string } | null;
}

function ModelsTab({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<ModelRow | 'new' | null>(null);

  const q = useQuery({
    queryKey: ['ref-models-all'],
    queryFn: () => api<Envelope<ModelRow[]>>('/ref/models'),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  return (
    <Section
      title="Гутлын загвар"
      action={canWrite ? <Button onClick={() => setEditing('new')}>＋ Загвар нэмэх</Button> : null}
      note="Загварын код өөрчлөгдөхгүй — түүх, тайлан үүн дээр тулгуурладаг"
    >
      <table className="w-full border-collapse text-[13px]">
        <thead><tr className="bg-well-2">
          <Th>Код</Th><Th>Брэнд</Th><Th>Нэр</Th><Th>Өнгө</Th><Th>Хүйс</Th>
          <Th>Материал</Th><Th num>Размер</Th><Th num>Баталгаа</Th><Th>Төлөв</Th>
        </tr></thead>
        <tbody>
          {q.data.data.map((m) => (
            <tr
              key={m.id}
              onClick={() => canWrite && setEditing(m)}
              className={`transition hover:bg-row-hover ${canWrite ? 'cursor-pointer' : ''}`}
            >
              <Td><span className="num">{m.code}</span></Td>
              <Td>{m.brand}</Td>
              <Td muted>{m.name}</Td>
              <Td muted>{m.colorway?.labelMn ?? '—'}</Td>
              <Td muted>{GENDER_LABEL[m.gender as keyof typeof GENDER_LABEL] ?? m.gender}</Td>
              <Td muted>{MATERIAL_LABEL[m.material] ?? m.material}</Td>
              <Td num muted>{m.sizeMin ?? '—'}–{m.sizeMax ?? '—'}</Td>
              <Td num>{formatQty(m.warrantyDays)} хоног</Td>
              <Td muted>{m.isActive === false ? 'Идэвхгүй' : 'Идэвхтэй'}</Td>
            </tr>
          ))}
        </tbody>
      </table>
      {q.data.data.length === 0 ? <Empty>Загвар алга</Empty> : null}

      {editing ? (
        <ModelForm
          model={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => { setEditing(null); await qc.invalidateQueries({ queryKey: ['ref-models-all'] }); }}
        />
      ) : null}
    </Section>
  );
}

function ModelForm({ model, onClose, onDone }: {
  model: ModelRow | null; onClose: () => void; onDone: () => void;
}) {
  const isNew = !model;
  const [code, setCode] = useState(model?.code ?? '');
  const [brand, setBrand] = useState(model?.brand ?? '');
  const [name, setName] = useState(model?.name ?? '');
  const [gender, setGender] = useState(model?.gender ?? 'UNISEX');
  const [material, setMaterial] = useState(model?.material ?? 'REINDEER');
  const [colorwayId, setColorway] = useState(model?.colorway?.id ?? '');
  const [sizeMin, setMin] = useState(model?.sizeMin ? String(model.sizeMin) : '');
  const [sizeMax, setMax] = useState(model?.sizeMax ? String(model.sizeMax) : '');
  const [warrantyDays, setDays] = useState(String(model?.warrantyDays ?? 365));
  const [warrantyTermsId, setTerms] = useState('');
  const [isUnique, setUnique] = useState(model?.isUnique ?? true);
  const [isActive, setActive] = useState(model?.isActive !== false);

  const colorways = useQuery({
    queryKey: ['lookups', 'COLORWAY'],
    queryFn: () => api<Envelope<{ id: string; labelMn: string }[]>>('/ref/lookups?category=COLORWAY&active=true'),
  });
  const terms = useQuery({
    queryKey: ['ref-terms'],
    queryFn: () => api<Envelope<{ id: string; version: string }[]>>('/ref/warranty-terms'),
  });

  const save = useMutation({
    mutationFn: () => {
      const common = {
        brand, name, gender, material, isUnique,
        ...(colorwayId ? { colorwayId } : {}),
        ...(sizeMin ? { sizeMin: Number(sizeMin) } : {}),
        ...(sizeMax ? { sizeMax: Number(sizeMax) } : {}),
        warrantyDays: Number(warrantyDays),
      };
      return isNew
        ? api('/ref/models', {
            method: 'POST',
            body: { code: code.toUpperCase(), ...common, warrantyTermsId },
          })
        : api(`/ref/models/${model.id}`, { method: 'PATCH', body: { ...common, isActive } });
    },
    onSuccess: onDone,
  });

  const ready = isNew ? code && brand && name && warrantyTermsId : brand && name;

  return (
    <Modal
      title={isNew ? 'Шинэ загвар' : `Загвар ${model.code}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!ready || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      {isNew ? (
        <Field label="Код (латин том үсэг)">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="TAIGA-BLK-M" className="num" />
        </Field>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Брэнд"><Input value={brand} onChange={(e) => setBrand(e.target.value)} /></Field>
        <Field label="Нэр"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Хүйс">
          <Select value={gender} onChange={setGender} options={
            Object.entries(GENDER_LABEL).map(([v, l]) => ({ value: v, label: l }))
          } />
        </Field>
        <Field label="Материал">
          <Select value={material} onChange={setMaterial} options={
            Object.entries(MATERIAL_LABEL).map(([v, l]) => ({ value: v, label: l }))
          } />
        </Field>
      </div>
      <Field label="Өнгө">
        <Select value={colorwayId} onChange={setColorway} placeholder="Сонгохгүй"
          options={(colorways.data?.data ?? []).map((c) => ({ value: c.id, label: c.labelMn }))} />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Хамгийн бага размер"><Input value={sizeMin} onChange={(e) => setMin(e.target.value)} inputMode="numeric" className="num" /></Field>
        <Field label="Хамгийн их размер"><Input value={sizeMax} onChange={(e) => setMax(e.target.value)} inputMode="numeric" className="num" /></Field>
        <Field label="Баталгаа (хоног)"><Input value={warrantyDays} onChange={(e) => setDays(e.target.value)} inputMode="numeric" className="num" /></Field>
      </div>
      {isNew ? (
        <Field label="Баталгааны нөхцөл">
          <Select value={warrantyTermsId} onChange={setTerms} placeholder="Сонгох…"
            options={(terms.data?.data ?? []).map((t) => ({ value: t.id, label: t.version }))} />
        </Field>
      ) : null}
      <div className="flex flex-wrap gap-4">
        <Check label="Давтагдашгүй нэгж" checked={isUnique} onChange={setUnique} />
        {!isNew ? <Check label="Идэвхтэй" checked={isActive} onChange={setActive} /> : null}
      </div>
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ============================================================ Жагсаалт (lookup)
/**
 * Захиалагчийн удирддаг жагсаалт — `lookups` хүснэгт, категори тус бүр.
 *
 * ⚠️ Өмнө нь «Сонголт» гэсэн НЭГ таб, дотроо категорийн dropdown-той
 *    байв — «зээлийн апп хаана нэмэх вэ» гэдгийг олдоггүй байсан
 *    (захиалагчийн цэгцлэл №15, №17). Одоо УТГААР нь таб: Зээлийн апп ·
 *    Шалтгаан · Өнгө. Хүснэгт, API, категорийн код хэвээр.
 * ⚠️ «Сонголт»-ыг ХАСАХГҮЙ: касс (`PAYMENT_PROVIDER`), буцаалт
 *    (`RETURN_REASON`), актлалт, гэмтэл, баталгаа бүгд эндээс уншина.
 * ⚠️ Устгах товч БАЙХГҮЙ — идэвхгүй болгоно. Өмнөх борлуулалт, буцаалт
 *    тэр мөрийг FK-оор заасаар байна.
 */
type LookupCategory =
  | 'PAYMENT_PROVIDER' | 'PROMOTION' | 'RETURN_REASON' | 'DEFECT_TYPE'
  | 'WRITE_OFF_REASON' | 'CLAIM_REJECT_REASON' | 'COLORWAY';

const LOOKUP_CATEGORIES: Record<LookupCategory, { label: string; about: string }> = {
  PAYMENT_PROVIDER: {
    label: 'Зээлийн апп',
    about: 'Кассанд «Зээлийн апп»-аар төлөхөд гарах жагсаалт (LendMN, Simple …). '
      + 'Шинэ апп гарвал энд нэмнэ — програм шинэчлэх шаардлагагүй. '
      + 'Идэвхгүй болговол кассанд гарахаа болино, өмнөх борлуулалт хэвээр.',
  },
  PROMOTION: {
    label: 'Урамшуулал',
    about: 'Кассанд худалдан авагчид өгсөн урамшуулал («Цахилгаан бараа» …). '
      + 'Кассчин шинэ нэр шивж зармагц энд нэмэгдэнэ. Идэвхгүй болговол кассанд '
      + 'гарахаа болино, өмнөх борлуулалт нэрээ хадгална.',
  },
  RETURN_REASON: {
    label: 'Буцаалт',
    about: 'Буцаалт, солилцоо бүртгэхэд заавал сонгох шалтгаан.',
  },
  DEFECT_TYPE: {
    label: 'Гэмтэл',
    about: 'Аяллын хайрцаг задлахад илэрсэн гэмтэл ба баталгааны нэхэмжлэлд сонгоно.',
  },
  WRITE_OFF_REASON: {
    label: 'Актлалт',
    about: 'Гутал актлах, тооллогын дутууг актлахад сонгоно.',
  },
  CLAIM_REJECT_REASON: {
    label: 'Нэхэмжлэлээс татгалзах',
    about: 'Баталгааны нэхэмжлэлээс татгалзахад сонгоно.',
  },
  COLORWAY: {
    label: 'Өнгө',
    about: 'Гутлын загварын өнгө — «Гутлын загвар» табын маягтад сонгоно.',
  },
};

/** «Шалтгаан» табын дэд жагсаалтууд — нэг дор, чипээр сэлгэнэ. */
const REASON_CATEGORIES: LookupCategory[] = [
  'RETURN_REASON', 'DEFECT_TYPE', 'WRITE_OFF_REASON', 'CLAIM_REJECT_REASON',
];

interface LookupRow {
  id: string; category: string; code: string;
  labelMn: string; labelRu: string | null; sortOrder: number; isActive?: boolean;
}

function LookupsTab({ title, categories, canWrite }: {
  title: string; categories: LookupCategory[]; canWrite: boolean;
}) {
  const qc = useQueryClient();
  const [category, setCategory] = useState<LookupCategory>(categories[0]!);
  const [editing, setEditing] = useState<LookupRow | 'new' | null>(null);

  const q = useQuery({
    queryKey: ['ref-lookups', category],
    queryFn: () => api<Envelope<LookupRow[]>>(`/ref/lookups?category=${category}`),
  });

  return (
    <Section
      title={title}
      about={LOOKUP_CATEGORIES[category].about}
      action={
        <div className="flex flex-wrap items-center gap-2">
          {categories.length > 1 ? (
            <Tabs
              value={category}
              onChange={setCategory}
              tabs={categories.map((c) => ({ key: c, label: LOOKUP_CATEGORIES[c].label }))}
              scroll
            />
          ) : null}
          {canWrite ? <Button onClick={() => setEditing('new')}>＋ Нэмэх</Button> : null}
        </div>
      }
      note="Эрэмбийг дараалалын дугаараар удирдана"
    >
      {q.isPending ? <Spinner /> : q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : (
        <>
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="bg-well-2">
              <Th num>Эрэмбэ</Th><Th>Код</Th><Th>Монгол нэр</Th><Th>Орос нэр</Th><Th>Төлөв</Th>
            </tr></thead>
            <tbody>
              {q.data.data.map((l) => (
                <tr key={l.id} onClick={() => canWrite && setEditing(l)}
                  className={`transition hover:bg-row-hover ${canWrite ? 'cursor-pointer' : ''}`}>
                  <Td num muted>{l.sortOrder}</Td>
                  <Td><span className="num">{l.code}</span></Td>
                  <Td>{l.labelMn}</Td>
                  <Td muted>{l.labelRu ?? '—'}</Td>
                  <Td muted>{l.isActive === false ? 'Идэвхгүй' : 'Идэвхтэй'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data.data.length === 0 ? <Empty>Жагсаалт хоосон</Empty> : null}
        </>
      )}

      {editing ? (
        <LookupForm
          lookup={editing === 'new' ? null : editing}
          category={category}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            // Касс, буцаалт, актлалтын маягт `['lookups', …]` түлхүүрээр уншдаг
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['ref-lookups'] }),
              qc.invalidateQueries({ queryKey: ['lookups'] }),
            ]);
          }}
        />
      ) : null}
    </Section>
  );
}

function LookupForm({ lookup, category, onClose, onDone }: {
  lookup: LookupRow | null; category: LookupCategory; onClose: () => void; onDone: () => void;
}) {
  const isNew = !lookup;
  const [code, setCode] = useState(lookup?.code ?? '');
  const [labelMn, setMn] = useState(lookup?.labelMn ?? '');
  const [labelRu, setRu] = useState(lookup?.labelRu ?? '');
  const [sortOrder, setSort] = useState(String(lookup?.sortOrder ?? 0));
  const [isActive, setActive] = useState(lookup?.isActive !== false);

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? api('/ref/lookups', {
            method: 'POST',
            body: {
              category, code: code.toUpperCase(), labelMn,
              ...(labelRu.trim() ? { labelRu: labelRu.trim() } : {}),
              sortOrder: Number(sortOrder) || 0,
            },
          })
        : api(`/ref/lookups/${lookup.id}`, {
            method: 'PATCH',
            body: {
              labelMn,
              ...(labelRu.trim() ? { labelRu: labelRu.trim() } : { labelRu: null }),
              sortOrder: Number(sortOrder) || 0,
              isActive,
            },
          }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={isNew ? 'Шинэ сонголт' : `Сонголт ${lookup.code}`}
      subtitle={LOOKUP_CATEGORIES[category].label}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!labelMn || (isNew && !code) || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      {isNew ? (
        <Field label="Код (латин том үсэг, доогуур зураас)">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="SOLE_CRACK" className="num" />
        </Field>
      ) : null}
      <Field label="Монгол нэр"><Input value={labelMn} onChange={(e) => setMn(e.target.value)} /></Field>
      <Field label="Орос нэр"><Input value={labelRu} onChange={(e) => setRu(e.target.value)} /></Field>
      <Field label="Эрэмбэ"><Input value={sortOrder} onChange={(e) => setSort(e.target.value)} inputMode="numeric" className="num" /></Field>
      {!isNew ? <Check label="Идэвхтэй" checked={isActive} onChange={setActive} /> : null}
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ============================================================ Ханш
interface FxRow { date: string; currency: string; rate: string; source: string }

function FxTab({ canWrite }: { canWrite: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [currency, setCurrency] = useState('RUB');

  const q = useQuery({
    queryKey: ['ref-fx', currency],
    queryFn: () => api<Envelope<FxRow[]>>(`/ref/fx?currency=${currency}`),
  });

  const gaps = useQuery({
    queryKey: ['ref-fx-gaps', currency],
    queryFn: () => api<{ data: string[]; meta?: { count: number } }>(`/ref/fx/gaps?currency=${currency}`),
  });

  return (
    <Section
      title="Валютын ханшийн түүх"
      action={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-full sm:w-[120px]">
            <Select value={currency} onChange={setCurrency} options={[
              { value: 'RUB', label: 'RUB' }, { value: 'USD', label: 'USD' }, { value: 'CNY', label: 'CNY' },
            ]} />
          </div>
          {canWrite ? <Button onClick={() => setAdding(true)}>＋ Ханш оруулах</Button> : null}
        </div>
      }
      note="Ханш бараа бүртгэх мөчид түгжигдэнэ — дараа өөрчилсөн ч хуучин бараанд нөлөөлөхгүй"
    >
      {(gaps.data?.data.length ?? 0) > 0 ? (
        <div className="mb-3">
          <ErrorNote>
            {formatQty(gaps.data!.data.length)} өдрийн ханш дутуу байна:{' '}
            {gaps.data!.data.slice(0, 5).map((d) => formatDate(d)).join(', ')}
            {gaps.data!.data.length > 5 ? '…' : ''}
          </ErrorNote>
        </div>
      ) : null}

      {q.isPending ? <Spinner /> : q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : (
        <>
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="bg-well-2"><Th num>Огноо</Th><Th>Валют</Th><Th num>Ханш</Th><Th>Эх сурвалж</Th></tr></thead>
            <tbody>
              {q.data.data.map((f) => (
                <tr key={`${f.date}-${f.currency}`} className="transition hover:bg-row-hover">
                  <Td num>{formatDate(f.date)}</Td>
                  <Td muted>{f.currency}</Td>
                  <Td num><strong>{formatRate(f.rate)}₮</strong></Td>
                  <Td muted>{f.source}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data.data.length === 0 ? <Empty>Ханш бүртгэгдээгүй</Empty> : null}
        </>
      )}

      {adding ? (
        <FxForm
          currency={currency}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await qc.invalidateQueries({ queryKey: ['ref-fx'] });
            await qc.invalidateQueries({ queryKey: ['ref-fx-gaps'] });
          }}
        />
      ) : null}
    </Section>
  );
}

function FxForm({ currency, onClose, onDone }: { currency: string; onClose: () => void; onDone: () => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rate, setRate] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api('/ref/fx', {
        method: 'POST',
        body: {
          currency,
          date: new Date(`${date}T00:00:00.000Z`).toISOString(),
          rate: String(Number(rate)),
          source: 'manual',
        },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`${currency} ханш оруулах`}
      subtitle="Ижил өдөр давхар оруулбал шинэчлэгдэнэ"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!Number(rate) || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      <Field label="Огноо"><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field label={`1 ${currency} = ? ₮`}>
        <Input value={rate} onChange={(e) => setRate(e.target.value)} inputMode="decimal" placeholder="43.62" className="num text-right" />
      </Field>
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ============================================================ Баталгааны нөхцөл
interface TermsRow { id: string; version: string; effectiveFrom: string; effectiveTo: string | null }

function TermsTab() {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const canWrite = can('ADMIN') && !readOnly;

  const q = useQuery({
    queryKey: ['ref-terms'],
    queryFn: () => api<Envelope<TermsRow[]>>('/ref/warranty-terms'),
  });

  return (
    <Section
      title="Баталгааны нөхцөлийн хувилбар"
      about={
        'Худалдан авагчид өгөх баталгааны бичвэр. Гутлын загвар бүр нэг хувилбартай; '
        + 'зарах мөчид тэр хувилбар борлуулалтын мөрөнд хөлдөж, баталгааны нэхэмжлэлд нотолгоо болно. '
        + 'Нөхцөл өөрчлөгдвөл шинэ хувилбар нэмнэ — өмнө зарагдсан гутал хуучин нөхцөлөөрөө үлдэнэ.'
      }
      action={canWrite ? <Button onClick={() => setAdding(true)}>＋ Шинэ хувилбар</Button> : null}
      note="Хуучин хувилбар засагдахгүй — 10 жилийн нотолгоо. Зөвхөн шинэ хувилбар нэмнэ"
    >
      {q.isPending ? <Spinner /> : q.error ? <ErrorNote>{(q.error as Error).message}</ErrorNote> : (
        <>
          <table className="w-full border-collapse text-[13px]">
            <thead><tr className="bg-well-2"><Th>Хувилбар</Th><Th num>Эхэлсэн</Th><Th num>Дууссан</Th><Th>Төлөв</Th></tr></thead>
            <tbody>
              {q.data.data.map((t) => (
                <tr key={t.id} className="transition hover:bg-row-hover">
                  <Td><span className="num">{t.version}</span></Td>
                  <Td num muted>{formatDate(t.effectiveFrom)}</Td>
                  <Td num muted>{t.effectiveTo ? formatDate(t.effectiveTo) : '—'}</Td>
                  <Td>
                    <span className={`rounded-full px-2.5 py-[3px] text-[11px] ${
                      t.effectiveTo ? 'bg-hair/60 text-ink-2' : 'bg-accent-100 text-accent-700'
                    }`}>
                      {t.effectiveTo ? 'Хуучирсан' : 'Хүчинтэй'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data.data.length === 0 ? <Empty>Нөхцөл алга</Empty> : null}
        </>
      )}

      {adding ? (
        <TermsForm onClose={() => setAdding(false)} onDone={async () => {
          setAdding(false); await qc.invalidateQueries({ queryKey: ['ref-terms'] });
        }} />
      ) : null}
    </Section>
  );
}

function TermsForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [version, setVersion] = useState('');
  const [bodyMn, setBody] = useState('');
  const [effectiveFrom, setFrom] = useState(new Date().toISOString().slice(0, 10));

  const save = useMutation({
    mutationFn: () =>
      api('/ref/warranty-terms', {
        method: 'POST',
        body: {
          version, bodyMn,
          effectiveFrom: new Date(`${effectiveFrom}T00:00:00.000Z`).toISOString(),
        },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      wide
      title="Баталгааны шинэ нөхцөл"
      subtitle="Өмнөх хувилбар автоматаар хаагдана"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!version || bodyMn.length < 10 || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      <Field label="Хувилбарын нэр"><Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="2027-v1-standard" className="num" /></Field>
      <Field label="Эхлэх огноо"><Input type="date" value={effectiveFrom} onChange={(e) => setFrom(e.target.value)} /></Field>
      <Field label="Нөхцөлийн бүтэн текст (монгол)">
        <Textarea rows={10} value={bodyMn} onChange={(e) => setBody(e.target.value)} />
      </Field>
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ============================================================ Төхөөрөмж
interface ActiveSession {
  id: string; userId: string; username: string; fullName: string;
  lastUsedAt: string; expiresAt: string;
}

interface DeviceRow {
  id: string; code: string; type: string; label: string;
  serialNumber: string | null; storeId: string | null; assignedToId: string | null;
  appVersion: string | null; osVersion: string | null;
  lastSyncAt: string | null; lastSeenAt: string | null;
  isBlocked: boolean; blockedAt: string | null;
  store: { code: string; name: string } | null;
  assignedTo: { username: string; fullName: string } | null;
  /** Одоо нэвтэрсэн ажилтан. Төхөөрөмж тутам нэг л идэвхтэй сесс. */
  activeSession: ActiveSession | null;
}

const DEVICE_TYPE_LABEL: Record<string, string> = {
  COLLECTOR_PHONE: 'Цуглуулагчийн утас',
  HANDHELD: 'Гар төхөөрөмж',
  POS: 'Касс',
  LABEL_PRINTER: 'Шошгоны принтер',
  WEB: 'Веб',
};

function DevicesTab() {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [blocking, setBlocking] = useState<DeviceRow | null>(null);
  const canWrite = can('ADMIN') && !readOnly;

  const q = useQuery({
    queryKey: ['ref-devices'],
    queryFn: () => api<Envelope<DeviceRow[]>>('/ref/devices'),
    enabled: can('ADMIN'),
  });

  /** Сүүлийн нийтэлсэн APK — хоцорсон аппыг тэмдэглэнэ (docs/ANDROID.md §7.4). */
  const release = useQuery({
    queryKey: ['mobile-release'],
    queryFn: () => api<Envelope<MobileReleaseOutput | null>>('/mobile/releases/latest'),
    enabled: can('ADMIN'),
  });
  const latest = release.data?.data ?? null;

  const refresh = () => qc.invalidateQueries({ queryKey: ['ref-devices'] });

  /**
   * Сесс хаах — төхөөрөмжийг хаахгүйгээр одоогийн нэвтрэлтийг л
   * унтраана. Ажилтан дараагийн удаа нэр, нууц үгээрээ дахин нэвтэрнэ.
   */
  const revoke = useMutation({
    mutationFn: (d: DeviceRow) => api(`/ref/devices/${d.id}/sessions/revoke`, { method: 'POST' }),
    onSuccess: () => void refresh(),
  });

  const unblock = useMutation({
    mutationFn: (id: string) => api(`/ref/devices/${id}/unblock`, { method: 'POST' }),
    onSuccess: () => void refresh(),
  });

  if (!can('ADMIN')) return <ErrorNote>Төхөөрөмжийн жагсаалт зөвхөн админд.</ErrorNote>;
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const outdated = (d: DeviceRow) => latest !== null && !d.isBlocked && isAppOutdated(d.appVersion, latest.versionCode);
  const outdatedCount = q.data.data.filter(outdated).length;

  return (
    <Section
      title="Төхөөрөмж"
      action={canWrite ? <Button onClick={() => setAdding(true)}>＋ Төхөөрөмж нэмэх</Button> : null}
      note="Ажилтан аппад нэр, нууц үгээрээ нэвтэрмэгц төхөөрөмж энд өөрөө бүртгэгдэнэ"
    >
      {/*
        Шинэчлэлийг утас өөрөө санал болгоно (нүүр дэлгэцийн тууз). Энд
        аль төхөөрөмж хоцорсныг л харуулна; «APK татах» нь шинэ төхөөрөмжид
        анх суулгахад — утасны хөтчөөр нээж татна.
      */}
      {latest ? (
        <p className="mb-3 text-[12.5px] text-ink-2">
          Сүүлийн хувилбар: <span className="num">build {latest.versionCode}</span> ·{' '}
          {formatDateTime(latest.publishedAt)}
          {outdatedCount > 0 ? (
            <> · <span className="text-alert-ink">{formatQty(outdatedCount)} төхөөрөмж шинэчлэгдээгүй</span></>
          ) : ' · бүгд шинэчлэгдсэн'}
          {' · '}
          <a href={latest.downloadUrl} className="text-accent-700 underline">APK татах</a>
        </p>
      ) : null}
      <table className="w-full border-collapse text-[13px]">
        <thead><tr className="bg-well-2">
          <Th>Код</Th><Th>Төрөл</Th><Th>Нэр</Th><Th>Салбар</Th><Th>Нэвтэрсэн</Th>
          <Th>Хувилбар</Th><Th num>Сүүлд холбогдсон</Th><Th>Төлөв</Th><Th> </Th>
        </tr></thead>
        <tbody>
          {q.data.data.map((d) => (
            <tr key={d.id} className={`transition hover:bg-row-hover ${d.isBlocked ? 'opacity-60' : ''}`}>
              <Td><span className="num">{d.code}</span></Td>
              <Td muted>{DEVICE_TYPE_LABEL[d.type] ?? d.type}</Td>
              <Td muted>{d.label}</Td>
              <Td muted>{d.store?.name ?? '—'}</Td>
              <Td muted>
                {d.activeSession ? (
                  <span title={`${d.activeSession.username} · ${formatDateTime(d.activeSession.expiresAt)} хүртэл`}>
                    {d.activeSession.fullName}
                  </span>
                ) : (
                  d.assignedTo?.fullName ?? '—'
                )}
              </Td>
              <Td muted className={`num ${outdated(d) ? 'text-alert-ink' : ''}`}>
                <span title={outdated(d) ? 'Шинэчлэгдээгүй. Шинэчлэгчтэй апп нүүр дэлгэцийн туузаар шинэчилнэ; түүнээс хуучин аппыг «APK татах»-аар эсвэл кабелиар' : undefined}>
                  {d.appVersion ?? '—'}{outdated(d) ? ' ↑' : ''}
                </span>
              </Td>
              <Td num muted>{d.lastSeenAt ? formatDateTime(d.lastSeenAt) : '—'}</Td>
              <td className="border-b border-hair/70 px-3 py-2.5">
                <span className={`rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${
                  d.isBlocked ? 'bg-alert-bg text-alert-ink' : 'bg-accent-100 text-accent-700'
                }`}>
                  {d.isBlocked ? 'Хаагдсан' : 'Идэвхтэй'}
                </span>
              </td>
              <td className="border-b border-hair/70 px-3 py-2 text-right whitespace-nowrap">
                {canWrite ? (
                  d.isBlocked ? (
                    <button type="button" onClick={() => unblock.mutate(d.id)}
                      className="inline-flex min-h-11 items-center rounded-full border border-hair px-2.5 py-1 text-[11px] hover:bg-well sm:min-h-0">
                      Нээх
                    </button>
                  ) : (
                    <span className="flex justify-end gap-1.5">
                      {d.activeSession ? (
                        <button type="button" onClick={() => revoke.mutate(d)} disabled={revoke.isPending}
                          className="inline-flex min-h-11 items-center rounded-full border border-hair px-2.5 py-1 text-[11px] hover:bg-well sm:min-h-0">
                          Сесс хаах
                        </button>
                      ) : null}
                      <button type="button" onClick={() => setBlocking(d)}
                        className="inline-flex min-h-11 items-center rounded-full border border-hair px-2.5 py-1 text-[11px] text-alert-ink hover:bg-alert-bg sm:min-h-0">
                        Хаах
                      </button>
                    </span>
                  )
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adding ? (
        <DeviceForm onClose={() => setAdding(false)} onDone={async () => { setAdding(false); await refresh(); }} />
      ) : null}


      {blocking ? (
        <BlockDevice
          device={blocking}
          onClose={() => setBlocking(null)}
          onDone={async () => { setBlocking(null); await refresh(); }}
        />
      ) : null}
    </Section>
  );
}

function DeviceForm({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [type, setType] = useState('COLLECTOR_PHONE');
  const [label, setLabel] = useState('');
  const [serialNumber, setSerial] = useState('');
  const [storeId, setStore] = useState('');

  const stores = useQuery({ queryKey: ['ref-stores'], queryFn: () => api<Envelope<Store[]>>('/ref/stores') });

  const save = useMutation({
    mutationFn: () =>
      api('/ref/devices', {
        method: 'POST',
        body: {
          code: code.toUpperCase(), type, label,
          ...(serialNumber.trim() ? { serialNumber: serialNumber.trim() } : {}),
          ...(storeId ? { storeId } : {}),
        },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="Шинэ төхөөрөмж"
      subtitle="Код нь хүн уншихад зориулсан нэр — нууц биш"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => save.mutate()} disabled={!code || !label || save.isPending}>
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      <Field label="Код"><Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="DEV-RU-02" className="num" /></Field>
      <Field label="Төрөл">
        <Select value={type} onChange={setType} options={
          Object.entries(DEVICE_TYPE_LABEL).map(([v, l]) => ({ value: v, label: l }))
        } />
      </Field>
      <Field label="Нэр"><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Орос — Батаа" /></Field>
      <Field label="Үйлдвэрийн дугаар"><Input value={serialNumber} onChange={(e) => setSerial(e.target.value)} className="num" /></Field>
      <Field label="Салбар">
        <Select value={storeId} onChange={setStore} placeholder="Хамаарахгүй"
          options={(stores.data?.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />
      </Field>
      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

function BlockDevice({ device, onClose, onDone }: {
  device: DeviceRow; onClose: () => void; onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const run = useMutation({
    mutationFn: () => api(`/ref/devices/${device.id}/block`, { method: 'POST', body: { reason: reason.trim() } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`${device.code} — хаах`}
      subtitle="Идэвхтэй сесс, ашиглаагүй токен бүгд тэр дороо цуцлагдана"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button variant="danger" onClick={() => run.mutate()} disabled={reason.trim().length < 3 || run.isPending}>
            {run.isPending ? 'Хааж байна…' : 'Хаах'}
          </Button>
        </>
      }
    >
      <Field label="Шалтгаан (заавал)">
        <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Жишээ: утас алдагдсан" />
      </Field>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

// ============================================================ Туслах
function Section({ title, about, action, note, children }: {
  title: string;
  /** «Энэ юу вэ?» — хаана хэрэглэгддэгийг гарчгийн доор хэлнэ */
  about?: string;
  action?: React.ReactNode; note?: string; children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <div className="ml-auto">{action}</div>
      </div>
      {about ? <p className="max-w-prose text-[12.5px] leading-relaxed text-ink-2">{about}</p> : null}
      {/*
        Энэ хуудасны 7 хүснэгт бүгд эндүүр дамжина. Бусад дэлгэцийн
        үндсэн жагсаалтууд `DataTable`-ээр карт болдог; лавлах нь
        админы тохиргооны дэлгэц (загвар, ханш, төхөөрөмж) тул карт
        болгох нь утга багатай — оронд нь ЭХНИЙ БАГАНА наалдана,
        ингэснээр хажуу тийш гүйлгэхэд аль мөр гэдэг нь харагдана.
      */}
      <div className="table-pin">{children}</div>
      {note ? <p className="text-[11.5px] text-ink-3">{note}</p> : null}
    </section>
  );
}

function Check({ label, checked, onChange }: {
  label: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-[13px] text-ink-2">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="size-4 accent-accent" />
      {label}
    </label>
  );
}

function Th({ children, num = false }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair px-3 py-2.5 text-[11.5px] font-medium tracking-wide
        text-ink-2 uppercase whitespace-nowrap ${num ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, num = false, muted = false, className = '' }: {
  children: React.ReactNode; num?: boolean; muted?: boolean; className?: string;
}) {
  return (
    <td
      {...(num ? { 'data-num': 'true' } : {})}
      // Кирилл нэр, тайлбар боогдоно — `nowrap` зөвхөн тоон баганад
      className={`border-b border-hair/70 px-3 py-2.5
        ${num ? 'num text-right whitespace-nowrap' : ''} ${muted ? 'text-ink-2' : ''} ${className}`}
    >
      {children}
    </td>
  );
}
